/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router";
import {
  ControlledInput,
  FormUpload,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useForm, SubmitHandler } from "react-hook-form";
import { useEffect, useMemo, useRef, useState } from "react";
import { useUploadImage } from "../../store/useUploadImage";
import { useAlert } from "../../store/useAlert";
import JoditEditor from "jodit-react";
import "./richtext.css";
import { useAppInfo } from "../../store/useAppInfo";
import { SketchPicker } from "react-color";

type Inputs = {
  en_title?: string;
  nl_title?: string;
  videoUrl?: string;
};

function EditAppInfoScreen() {
  /** Refs */
  const editorRef = useRef(null);
  /** Hooks */
  const params = useParams();
  const { setAlert } = useAlert();
  const {
    getAppInfo,
    appInfo,
    updateAppInfo,
    updatedAppInfo,
    clearUpdatedAppInfo,
  } = useAppInfo();
  const { uploadImage, clearUploadedImage, uploadedImage, setUploadedImage } =
    useUploadImage();
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Inputs>();
  /** State */
  const [image, setImage] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [enContent, setEnContent] = useState("");
  const [nlContent, setNlContent] = useState("");
  const [colorValue, setColorValue] = useState<string>("");
  const [color, setColor] = useState<string>("");
  const [showColorPicker, setShowColorPicker] = useState<boolean>(false);

  const options = [
    "bold",
    "italic",
    // "|",
    // "ul",
    // "ol",
    "|",
    "outdent",
    "indent",
    "align",
    "|",
    "image",
    "|",
    "hr",
    "|",
    // "table",
    "link",
    "|",
    "undo",
    "redo",
  ];

  const config = useMemo(
    () => ({
      readonly: false,
      placeholder: "",
      defaultActionOnPaste: "insert_as_html",
      defaultLineHeight: 1.5,
      enter: "div",
      // options that we defined in above step.
      buttons: options,
      buttonsMD: options,
      buttonsSM: options,
      buttonsXS: options,
      statusbar: false,
      sizeLG: 900,
      sizeMD: 700,
      sizeSM: 400,
      toolbarAdaptive: false,
    }),
    [options]
  );

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    if (color === "") {
      setAlert({
        isOpen: true,
        message: "Please add color.",
        severity: "error",
      });
      setSubmitted(true);
      return;
    }
    if (!uploadedImage) {
      setAlert({
        isOpen: true,
        message: "Please upload an image.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    setSubmitted(false);
    const body = {
      ...data,
      color,
      title: data?.nl_title,
      image: uploadedImage,
      richText: nlContent,
      en_richText: enContent,
      nl_richText: nlContent,
      // Empty string clears the video; backend normalises to null.
      videoUrl: data?.videoUrl?.trim() ?? "",
    };
    updateAppInfo(params?.id as string, body);
  };

  const goBack = () => navigate(-1);

  useEffect(() => {
    getAppInfo(params?.id as string);
    return () => {
      clearUploadedImage();
      clearUpdatedAppInfo();
    };
  }, []);

  useEffect(() => {
    if (appInfo) {
      if (!appInfo?.en_title) {
        setValue("en_title", appInfo?.title);
      } else {
        setValue("en_title", appInfo?.en_title);
      }
      if (!appInfo?.nl_title) {
        setValue("nl_title", appInfo?.title);
      } else {
        setValue("nl_title", appInfo?.nl_title);
      }
      if (!appInfo?.nl_richText) {
        setNlContent(appInfo?.richText ?? "");
      } else {
        setNlContent(appInfo.nl_richText ?? "");
      }
      if (!appInfo?.en_richText) {
        setEnContent(appInfo?.richText ?? "");
      } else {
        setEnContent(appInfo.en_richText ?? "");
      }
      setValue("videoUrl", appInfo?.videoUrl ?? "");
      // setContent(appInfo.richText as string);
      setColor(appInfo.color as string);
      setUploadedImage(appInfo.image as string);
    }
  }, [appInfo]);

  useEffect(() => {
    if (updatedAppInfo) {
      navigate(-1);
    }
  }, [updatedAppInfo]);

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
    }
  };

  const onUploadImage = () => {
    if (!image) return;
    uploadImage(image);
  };

  const onColorChange = (color: any) => setColorValue(color.hex);
  const onColorChangeComplete = (color: any) => {
    setColor(color.hex);
    setShowColorPicker(false);
  };

  const imageError = submitted && !image;
  const colorError =
    (Object.values(errors)?.length && color === "") ||
    (submitted && color === "");

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Add new Recycling Info
        </h1>
      </div>
      <div className="flex flex-col sm:flex-row">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full sm:w-1/2 bg-white border border-gray-border drop-shadow-md mb-1 sm:mb-0"
        >
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              New Recycling Info
            </p>
          </div>
          <div className="w-full px-7 py-7">
            <ControlledInput
              control={control}
              error={Boolean(errors?.en_title)}
              label="Title (English version)"
              placeholder="Title"
              name="en_title"
              rules={{ required: true }}
            />
            <ControlledInput
              control={control}
              error={Boolean(errors?.nl_title)}
              label="Title (Dutch version)"
              placeholder="Title"
              name="nl_title"
              rules={{ required: true }}
            />
            <ControlledInput
              control={control}
              error={Boolean(errors?.videoUrl)}
              label="YouTube video URL (optional)"
              placeholder="https://www.youtube.com/watch?v=..."
              name="videoUrl"
              rules={{ required: false }}
            />
            {/* Color */}
            <div className="mb-4">
              <p className={`font-satoshi text-dark text-sm font-medium mb-3`}>
                Add color
              </p>
              <div
                className={`w-full h-[46px] px-4 bg-sky-lighter border ${
                  colorError ? "border-red-base" : "border-gray-border"
                } ${
                  colorError
                    ? "focus:border-red-base"
                    : "focus:border-focused-border"
                } rounded font-satoshi text-base text-dark font-medium outline-none flex flex-row items-center justify-between`}
              >
                <div className="flex flex-row items-center w-[100%] h-[100%]">
                  {color !== "" ? (
                    <div
                      onClick={() => {
                        setColor("");
                        setShowColorPicker(false);
                      }}
                      style={{ backgroundColor: color }}
                      className={`w-[15px] h-[15px] mr-1 cursor-pointer rounded`}
                    />
                  ) : null}
                </div>
                <button
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  type="button"
                  className="px-[8px] py-[3px] bg-primary-base font-satoshi font-medium text-white text-sm rounded"
                >
                  {color !== "" ? "Edit" : "Add"}
                </button>
              </div>
            </div>
            {showColorPicker ? (
              <SketchPicker
                color={colorValue}
                onChangeComplete={onColorChangeComplete}
                onChange={onColorChange}
              />
            ) : null}
            <p className={`font-satoshi text-dark text-sm font-medium mb-3`}>
              Description (English version)
            </p>
            <div className="mb-3">
              <JoditEditor
                ref={editorRef}
                value={enContent}
                className="h-[500px]"
                // @ts-ignore
                config={config}
                onBlur={(newContent) => setEnContent(newContent)} // preferred to use only this option to update the content for performance reasons
                onChange={(newContent) => {}}
              />
            </div>
            <p className={`font-satoshi text-dark text-sm font-medium mb-3`}>
              Description (Dutch version)
            </p>
            <div className="mb-3">
              <JoditEditor
                ref={editorRef}
                value={nlContent}
                className="h-[500px]"
                // @ts-ignore
                config={config}
                onBlur={(newContent) => setNlContent(newContent)} // preferred to use only this option to update the content for performance reasons
                onChange={(newContent) => {}}
              />
            </div>
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Cancel" />
              <PrimaryButton type="submit" className="ml-4" label="Save" />
            </div>
          </div>
        </form>
        <div
          className={`w-full sm:w-1/3 sm:ml-8 bg-white border border-gray-border drop-shadow-md h-[360px]`}
        >
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Recycling info photo
            </p>
          </div>
          <div className="w-full px-7 py-7">
            <FormUpload
              onChange={onImageChange}
              value={image}
              error={imageError}
              imageUrl={uploadedImage as string}
            />
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton onClick={goBack} label="Cancel" />
              <PrimaryButton
                onClick={onUploadImage}
                className="ml-4"
                label="Save"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(EditAppInfoScreen);
